/**
 * Settings Page
 *
 * User preferences and settings page.
 * Preferred date format and timezone normalise date display across the app and reports.
 */

import React, { useMemo } from 'react'
import { useSettings, DateFormat } from '@/contexts/SettingsContext'
import MainLayout from '@/components/layout/MainLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAppTheme, type ThemePreference } from '@/contexts/AppThemeContext'
import { PREFERRED_DATE_FORMATS, formatDateByPreference, getTimeZoneOptions } from '@/utils/dateFormat'

const DATE_FORMAT_LABELS: Record<DateFormat, string> = {
  'DD/MM/YYYY': 'DD/MM/YYYY (Day/Month/Year)',
  'MM/DD/YYYY': 'MM/DD/YYYY (Month/Day/Year)',
  'YYYY/MM/DD': 'YYYY/MM/DD (Year/Month/Day)',
  'DD-MM-YYYY': 'DD-MM-YYYY (Day-Month-Year)',
  'MM-DD-YYYY': 'MM-DD-YYYY (Month-Day-Year)',
  'YYYY-MM-DD': 'YYYY-MM-DD (ISO)',
}

const THEME_PREFERENCE_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

export default function Settings() {
  const { settings, updateDateFormat, updateTimezone, updateUse24Hour } = useSettings()
  const { theme, themePreference, setThemePreference } = useAppTheme()
  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), [])

  const dateFormats = PREFERRED_DATE_FORMATS.map((value) => ({
    value,
    label: DATE_FORMAT_LABELS[value],
  }))

  return (
    <MainLayout title="Settings">
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            <Card className={theme === 'dark'
              ? 'bg-nb-surface-1 border-nb-border'
              : 'bg-white border-gray-200'
            }>
              <CardHeader>
                <CardTitle className={`text-xl ${
                  theme === 'dark' ? 'text-nb-text-primary' : 'text-gray-900'
                }`}>Display Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-nb-text-secondary' : 'text-gray-700'
                  }`}>
                    Theme
                  </label>
                  <p className={`text-sm mb-3 ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-600'
                  }`}>
                    Light, dark, or follow your device setting
                  </p>
                  <Select
                    value={themePreference}
                    onValueChange={(value) => setThemePreference(value as ThemePreference)}
                  >
                    <SelectTrigger
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary shadow-sm'
                        : 'border border-gray-300 bg-white text-gray-900 shadow-sm'
                      }
                    >
                      <SelectValue placeholder="Theme" />
                    </SelectTrigger>
                    <SelectContent
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary'
                        : 'border border-gray-200 bg-white text-gray-900'
                      }
                    >
                      {THEME_PREFERENCE_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className={theme === 'dark'
                            ? 'focus:bg-nb-surface-3 focus:text-nb-text-primary'
                            : 'focus:bg-gray-100 focus:text-gray-900'
                          }
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-nb-text-secondary' : 'text-gray-700'
                  }`}>
                    Preferred date format
                  </label>
                  <p className={`text-sm mb-3 ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-600'
                  }`}>
                    How dates are displayed across the app and reports
                  </p>
                  <Select
                    value={settings.dateFormat}
                    onValueChange={(value) => updateDateFormat(value as DateFormat)}
                  >
                    <SelectTrigger
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary shadow-sm'
                        : 'border border-gray-300 bg-white text-gray-900 shadow-sm'
                      }
                    >
                      <SelectValue placeholder="Select date format" />
                    </SelectTrigger>
                    <SelectContent
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary'
                        : 'border border-gray-200 bg-white text-gray-900'
                      }
                    >
                      {dateFormats.map((format) => (
                        <SelectItem
                          key={format.value}
                          value={format.value}
                          className={theme === 'dark'
                            ? 'focus:bg-nb-surface-3 focus:text-nb-text-primary'
                            : 'focus:bg-gray-100 focus:text-gray-900'
                          }
                        >
                          {format.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className={`mt-3 text-xs ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-500'
                  }`}>
                    Example: today will be displayed as{' '}
                    {formatDateByPreference(new Date(), settings.dateFormat, { timeZone: settings.timezone })}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-nb-text-secondary' : 'text-gray-700'
                  }`}>
                    Time format
                  </label>
                  <p className={`text-sm mb-3 ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-600'
                  }`}>
                    Choose how times are displayed across the app
                  </p>
                  <Select
                    value={settings.use24Hour ? '24' : '12'}
                    onValueChange={(value) => updateUse24Hour(value === '24')}
                  >
                    <SelectTrigger
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary shadow-sm'
                        : 'border border-gray-300 bg-white text-gray-900 shadow-sm'
                      }
                    >
                      <SelectValue placeholder="Select time format" />
                    </SelectTrigger>
                    <SelectContent
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary'
                        : 'border border-gray-200 bg-white text-gray-900'
                      }
                    >
                      <SelectItem
                        value="24"
                        className={theme === 'dark'
                          ? 'focus:bg-nb-surface-3 focus:text-nb-text-primary'
                          : 'focus:bg-gray-100 focus:text-gray-900'
                        }
                      >
                        24-hour
                      </SelectItem>
                      <SelectItem
                        value="12"
                        className={theme === 'dark'
                          ? 'focus:bg-nb-surface-3 focus:text-nb-text-primary'
                          : 'focus:bg-gray-100 focus:text-gray-900'
                        }
                      >
                        12-hour (AM/PM)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className={`mt-3 text-xs ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-500'
                  }`}>
                    Example with time:{' '}
                    {formatDateByPreference(new Date(), settings.dateFormat, { showTime: true, timeZone: settings.timezone, use24Hour: settings.use24Hour })}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-nb-text-secondary' : 'text-gray-700'
                  }`}>
                    Timezone
                  </label>
                  <p className={`text-sm mb-3 ${
                    theme === 'dark' ? 'text-nb-text-muted' : 'text-gray-600'
                  }`}>
                    Timezone used for displaying dates and times across the app
                  </p>
                  <Select
                    value={settings.timezone}
                    onValueChange={updateTimezone}
                  >
                    <SelectTrigger
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary shadow-sm'
                        : 'border border-gray-300 bg-white text-gray-900 shadow-sm'
                      }
                    >
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent
                      className={theme === 'dark'
                        ? 'border border-nb-border bg-[var(--nb-surface-2)] text-nb-text-primary max-h-[min(20rem,70vh)]'
                        : 'border border-gray-200 bg-white text-gray-900 max-h-[min(20rem,70vh)]'
                      }
                    >
                      {timeZoneOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className={theme === 'dark'
                            ? 'focus:bg-nb-surface-3 focus:text-nb-text-primary'
                            : 'focus:bg-gray-100 focus:text-gray-900'
                          }
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}


